import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Trophy, TrendingUp, Users, Target, Calendar,
  Loader2, RefreshCw, ChevronDown, ChevronRight, ExternalLink, Sparkles,
} from "lucide-react";
import api from "@/lib/api";

const METRIC_LABEL = {
  contract_value: "Valor do contrato",
  deposit_total_paid: "Caução paga",
  contracts_count: "Contratos fechados",
  monthly_value_annualised: "Valor do contrato",
};

const MONTHS_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function fmtEUR(v) {
  return new Intl.NumberFormat("pt-PT", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(Number(v || 0));
}

function fmtNumber(v) {
  return new Intl.NumberFormat("pt-PT").format(Number(v || 0));
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("pt-PT");
}

function fmtValue(value, metric) {
  if (metric === "contracts_count") return fmtNumber(value);
  return fmtEUR(value);
}

// ---- Individual objective row ---------------------------------------------
function ObjectiveRow({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const { objective, achievement, my_converted, my_value, my_bonus, my_leads } = entry;
  const metric = objective.target_metric;
  const targetMet = achievement.target_met;
  const pct = achievement.achievement_percentage;
  const width = Math.min(100, Math.max(0, pct));

  return (
    <div className={`rounded-xl border-2 ${targetMet ? "border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))]/40" : "border-border bg-card"}`} data-testid={`me-obj-${objective.id}`}>
      <div className="p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
              {objective.office_name}
            </div>
            <h3 className="font-display text-xl truncate">{objective.name}</h3>
            <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
              <Calendar className="w-3 h-3" /> {fmtDate(objective.start_date)} → {fmtDate(objective.end_date)}
              <span>·</span>
              <span>Bónus: {objective.bonus_percentage}%</span>
            </div>
          </div>
          {targetMet && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] font-bold">
              <Trophy className="w-3 h-3" /> Meta atingida
            </span>
          )}
        </div>

        {/* Escritório progress */}
        <div className="mb-4">
          <div className="flex justify-between text-[11px] mb-1">
            <span className="text-muted-foreground">Progresso do escritório</span>
            <span className={`font-semibold ${targetMet ? "text-[hsl(var(--success))]" : "text-foreground"}`}>
              {fmtValue(achievement.achieved_value, metric)} / {fmtValue(achievement.target_value, metric)}
              {" · "}
              {pct.toFixed(1)}%
            </span>
          </div>
          <div className="h-2 bg-muted/40 rounded-full overflow-hidden border border-border">
            <div
              className={`h-full transition-all duration-700 rounded-full ${
                targetMet ? "bg-emerald-500" : pct >= 75 ? "bg-foreground" : "bg-foreground"
              }`}
              style={{ width: `${width}%` }}
            />
          </div>
        </div>

        {/* My contribution */}
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className="rounded-lg bg-muted/40 p-3">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Meus convertidos</div>
            <div className="text-lg font-bold text-foreground" data-testid={`me-obj-converted-${objective.id}`}>
              {my_converted}
            </div>
          </div>
          <div className="rounded-lg bg-muted/40 p-3">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Meu valor</div>
            <div className="text-lg font-bold text-foreground" data-testid={`me-obj-value-${objective.id}`}>
              {fmtValue(my_value, metric)}
            </div>
          </div>
          <div className={`rounded-lg p-3 ${targetMet ? "bg-[hsl(var(--success-bg))]" : "bg-muted/40"}`}>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Meu bónus</div>
            <div className={`text-lg font-bold ${targetMet ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`} data-testid={`me-obj-bonus-${objective.id}`}>
              {fmtEUR(my_bonus)}
            </div>
          </div>
        </div>

        {!targetMet && (
          <p className="text-xs text-muted-foreground italic">
            O bónus só é distribuído quando o escritório atinge a meta ({fmtValue(achievement.target_value, metric)}).
          </p>
        )}

        {my_leads.length > 0 && (
          <button
            onClick={() => setExpanded(e => !e)}
            className="mt-3 inline-flex items-center gap-1 text-xs text-foreground hover:underline font-semibold"
            data-testid={`me-obj-toggle-${objective.id}`}
          >
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            Ver {my_leads.length} lead{my_leads.length === 1 ? "" : "s"} convertido{my_leads.length === 1 ? "" : "s"}
          </button>
        )}
      </div>

      {expanded && (
        <div className="border-t border-border bg-muted/40 px-5 py-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                <th className="text-left py-2 font-semibold">Cliente</th>
                <th className="text-left py-2 font-semibold">Beneficiário</th>
                <th className="text-left py-2 font-semibold">Contrato</th>
                <th className="text-right py-2 font-semibold">Valor</th>
                <th className="text-right py-2 font-semibold">Lead</th>
              </tr>
            </thead>
            <tbody>
              {my_leads.map(l => (
                <tr key={l.lead_id} className="border-t border-border">
                  <td className="py-1.5">{l.contact_name}</td>
                  <td className="py-1.5">{l.beneficiary_name || "—"}</td>
                  <td className="py-1.5 text-muted-foreground">{fmtDate(l.signed_at)}</td>
                  <td className="py-1.5 text-right font-semibold">{fmtValue(l.value, metric)}</td>
                  <td className="py-1.5 text-right">
                    <Link to={`/leads/${l.lead_id}`} className="inline-flex items-center gap-1 text-foreground hover:underline">
                      Abrir <ExternalLink className="w-2.5 h-2.5" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- PAGE ------------------------------------------------------------------
export default function MyBonuses() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Modes:
  //   "active" → apenas objectivos activos (default)
  //   "all"    → todos os objectivos onde participei
  //   "month"  → filtra por year + month
  const now = new Date();
  const [mode, setMode] = useState("active");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12

  const years = useMemo(() => {
    // 3 anos para trás, ano actual, 1 ano para a frente
    const y = now.getFullYear();
    return [y - 3, y - 2, y - 1, y, y + 1];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (mode === "active") {
        params.set("active_only", "true");
      } else if (mode === "all") {
        params.set("active_only", "false");
      } else if (mode === "month") {
        params.set("active_only", "false");
        params.set("year", String(year));
        params.set("month", String(month));
      }
      const r = await api.get(`/me/bonuses?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a carregar bónus");
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, [mode, year, month]);  // eslint-disable-line

  if (loading) {
    return (
      <div className="p-8 lg:p-12 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> A carregar…
      </div>
    );
  }

  if (!data) return null;

  const periodLabel = mode === "month"
    ? `${MONTHS_PT[month - 1]} ${year}`
    : mode === "all"
      ? "Histórico completo"
      : "Objectivos activos";

  return (
    <div className="p-8 lg:p-12 max-w-4xl mx-auto space-y-6" data-testid="my-bonuses-root">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2 flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-foreground" /> Os meus bónus
          </div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Bem-vindo, {data.user_name?.split(" ")[0] || "operador"}!</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Este é o teu progresso pessoal nos objectivos onde tens leads.
            O bónus só é distribuído quando o escritório atinge a meta.
          </p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-border hover:bg-muted/40 disabled:opacity-50 flex-shrink-0"
          data-testid="me-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Actualizar
        </button>
      </div>

      {/* Period selector */}
      <div className="rounded-xl border border-border bg-card p-4 flex flex-wrap items-center gap-3" data-testid="me-period-selector">
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold flex items-center gap-1">
          <Calendar className="w-3 h-3" /> Período em análise
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setMode("active")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              mode === "active" ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted/40"
            }`}
            data-testid="me-mode-active"
          >
            Activos
          </button>
          <button
            onClick={() => setMode("all")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              mode === "all" ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted/40"
            }`}
            data-testid="me-mode-all"
          >
            Histórico
          </button>
          <button
            onClick={() => setMode("month")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              mode === "month" ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted/40"
            }`}
            data-testid="me-mode-month"
          >
            Por mês
          </button>
        </div>
        {mode === "month" && (
          <div className="flex items-center gap-2">
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value, 10))}
              className="px-3 py-1.5 rounded-lg text-xs border border-border bg-background focus:outline-none focus:ring-2 focus:ring-foreground/20"
              data-testid="me-month-select"
            >
              {MONTHS_PT.map((label, idx) => (
                <option key={idx + 1} value={idx + 1}>{label}</option>
              ))}
            </select>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="px-3 py-1.5 rounded-lg text-xs border border-border bg-background focus:outline-none focus:ring-2 focus:ring-foreground/20"
              data-testid="me-year-select"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        )}
        <div className="ml-auto text-xs text-muted-foreground">
          A ver: <span className="font-semibold text-foreground">{periodLabel}</span>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-border bg-card p-5" data-testid="me-kpi-converted">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-foreground" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Leads convertidos</span>
          </div>
          <div className="font-display text-3xl text-foreground">{data.total_converted}</div>
          <div className="text-xs text-muted-foreground mt-1">{periodLabel}</div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5" data-testid="me-kpi-objectives">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-foreground" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Objectivos</span>
          </div>
          <div className="font-display text-3xl text-foreground">{data.objectives.length}</div>
          <div className="text-xs text-muted-foreground mt-1">{periodLabel}</div>
        </div>

        <div className="rounded-xl border-2 border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))]/60 p-5" data-testid="me-kpi-bonus">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-4 h-4 text-[hsl(var(--success))]" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Meu bónus</span>
          </div>
          <div className="font-display text-3xl text-[hsl(var(--success))]">{fmtEUR(data.total_bonus_earned)}</div>
          <div className="text-xs text-muted-foreground mt-1">Já garantido nas metas atingidas</div>
        </div>
      </div>

      {/* Objectives */}
      {data.objectives.length === 0 ? (
        <div className="border-2 border-dashed border-border rounded-2xl p-12 text-center bg-card" data-testid="me-empty">
          <TrendingUp className="w-10 h-10 mx-auto text-foreground mb-3" />
          <div className="font-display text-2xl mb-2">
            {mode === "month" ? "Sem contribuição neste mês" : "Ainda sem objectivos"}
          </div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {mode === "active"
              ? "Não há objectivos a decorrer neste momento. Assim que a direcção definir uma meta, aparecerá aqui em tempo real."
              : mode === "month"
                ? `Não converteste leads em ${MONTHS_PT[month - 1]} de ${year}. Escolhe outro mês para ver o teu histórico.`
                : "Ainda não tens leads convertidos em nenhum objectivo. À medida que os teus contratos fecharem, o teu progresso aparecerá aqui."}
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="me-objectives-list">
          {data.objectives.map(entry => (
            <ObjectiveRow key={entry.objective.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}
